// Libraries
import { Box } from '@chakra-ui/react';
// Components
import { Label } from '@atoms/index';
import { RatingsDropdown, RatingsMenu } from '@molecules/index';
import { FilterFactory, FilterVariant } from '@organisms/index';
// Utils
import text from '@translations/en.json';
// Types
import { TableFiltersRatingsProps } from './TableFiltersRatings.types';

/**
 * TableFiltersRatings component displays filters specific to CII ratings for table columns.
 *
 * @component
 * @template TableBody - The type of the table body data.
 * @template ColumnNames - The type of the column names.
 * @param {TableFiltersRatingsProps<TableBody, ColumnNames>} props - The component props.
 * @param {FilterOptionsMap<ColumnNames>} props.filterOptionsMap - Map of filter options for each column.
 * @param {ColumnsGroup<TableBody>} props.columnsGroup -  Object representing the columns of the table.
 * @returns {ReactElement} The TableFiltersRatings component.
 */
export const TableFiltersRatings = <TableBody, ColumnNames extends string>({
  filterOptionsMap,
  ciiColumns,
  variant,
}: TableFiltersRatingsProps<TableBody, ColumnNames>) => {
  /**
   * Determines whether any of the CII columns are partially filtered.
   */
  const filtersCount = ciiColumns.reduce<number>((acc, curr) => {
    const filteredValues = (curr.getFilterValue() as string[]) ?? [];
    return acc + filteredValues.length;
  }, 0);
  const isPartiallyFiltered = filtersCount > 0;
  const messageText = filtersCount === 1 ? text.cii_rating : text.cii_ratings;
  const messageCount = isPartiallyFiltered ? String(filtersCount) : text.all;
  const message = `${messageCount} ${messageText}`;

  const handleSelectAll = () => {
    ciiColumns.forEach((column) => column.setFilterValue(undefined));
  };

  const getFilterElements = () =>
    ciiColumns.map((column) =>
      column.getCanFilter() ? (
        <Box key={`${column.id}_filter_container`} w='full' borderRight='greyCheck' _last={{ borderRight: 'none' }}>
          <FilterFactory<TableBody, ColumnNames>
            column={column}
            columnId={column.id}
            filterOptionsMap={filterOptionsMap}
            key={`${column.id}_filter`}
            variant={variant}
          />
        </Box>
      ) : null
    );

  return variant === FilterVariant.VISIBLE ? (
    <Label label={text.filter_labels.cii_rating}>
      <RatingsDropdown
        message={message}
        isPartiallyFiltered={isPartiallyFiltered}
        handleSelectAll={handleSelectAll}
        w={50}
      >
        {getFilterElements()}
      </RatingsDropdown>
    </Label>
  ) : (
    <RatingsMenu isPartiallyFiltered={isPartiallyFiltered} handleSelectAll={handleSelectAll}>
      {getFilterElements()}
    </RatingsMenu>
  );
};
